import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {RecordAuditResultValue, RecordAuditScoreResponseBody} from '@dms/api/core';

/**
 * Result shape required for scoring — only the two fields needed.
 */
export interface RecordAuditScorableResult {
  weight: number;
  value: RecordAuditResultValue | null;
}

/**
 * Live FE score recompute. Mirrors the BE formula:
 *
 *   okPoints  = Σ weight where value=OK
 *   maxPoints = Σ weight where value ∈ {OK, BAD}    (NA excluded both sides)
 *   pct       = round(ok / max * 100), null when max=0
 *   passed    = pct >= threshold, null when pct null
 */
export const computeRecordAuditScore = (
  results: ReadonlyArray<RecordAuditScorableResult>,
  threshold: number
): RecordAuditScoreResponseBody => {
  let okPoints = 0;
  let maxPoints = 0;

  for (const result of results) {
    const points = match(result.value)
      .with('OK', () => ({ok: result.weight, max: result.weight}))
      .with('BAD', () => ({ok: 0, max: result.weight}))
      .with('NA', null, () => ({ok: 0, max: 0}))
      .exhaustive();
    okPoints += points.ok;
    maxPoints += points.max;
  }

  const percentage = maxPoints === 0 ? null : Math.round((okPoints / maxPoints) * 100);
  const passed = isNil(percentage) ? null : percentage >= threshold;

  return {okPoints, maxPoints, percentage, passed};
};
